export type DashboardState = {
  connected: number;          // active chat sessions (heartbeat-based, 90s grace)
  dashboard_viewers: number;  // /ws/dashboard subscribers (FYI, not shown)
  total_queries: number;
  mode_a_count: number;
  mode_b_count: number;
  refused_count: number;
  cache_hits: number;
  cache_size: number;
  feed: {
    sid: string;
    display_name: string;
    question: string;
    answer_summary: string;
    mode: 'A' | 'B' | 'refused';
    ts: number;
  }[];
  predictions_count: number;
  predictions_by_university: { university: string; count: number }[];
  provider: 'anthropic' | 'openai';
  mode_b_model: 'sonnet' | 'opus';
  paused: boolean;
  circuit_open: boolean;
  budget_used_usd: number;
  reveal_mode: boolean;
  throttle_ms: number;
  per_session_rate_per_min: number;
  auto_fallback_enabled: boolean;
  fallback_reason: string | null;
  turnstile_enabled: boolean;
  testing_mode: boolean;
  allow_off_topic: boolean;
  feedback_count: number;
};

export type DashboardMessage = { type: 'state'; state: DashboardState };

export class ReconnectingWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private listeners: ((msg: DashboardMessage) => void)[] = [];
  private statusListeners: ((status: 'connecting' | 'open' | 'closed') => void)[] = [];
  private shouldReconnect = true;
  private backoffMs = 500;
  private readonly maxBackoffMs = 10000;

  constructor(url: string) { this.url = url; }

  connect() {
    this.shouldReconnect = true;
    this.emitStatus('connecting');
    this.ws = new WebSocket(this.url);
    this.ws.onopen = () => {
      this.backoffMs = 500;
      this.emitStatus('open');
    };
    this.ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as DashboardMessage;
        for (const l of this.listeners) l(msg);
      } catch { /* ignored */ }
    };
    this.ws.onclose = () => {
      this.emitStatus('closed');
      if (this.shouldReconnect) {
        setTimeout(() => this.connect(), this.backoffMs);
        this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
      }
    };
    this.ws.onerror = () => { /* onclose handles reconnect */ };
  }

  close() {
    this.shouldReconnect = false;
    this.ws?.close();
  }

  onMessage(cb: (msg: DashboardMessage) => void) {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter((l) => l !== cb); };
  }

  onStatus(cb: (status: 'connecting' | 'open' | 'closed') => void) {
    this.statusListeners.push(cb);
    return () => { this.statusListeners = this.statusListeners.filter((l) => l !== cb); };
  }

  private emitStatus(s: 'connecting' | 'open' | 'closed') {
    for (const l of this.statusListeners) l(s);
  }
}

